import type { SerializedTableCellNode } from "@lexical/table";
import type {
  DOMConversionMap,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
} from "lexical";
import { makeColumnPaddings } from "../../../email-design/util";
import type { StyleableColumnConfig } from "../utils/use-styleable-columns";
import type { SerializedStylableTableCellNode } from "./stylable-table-cell-node";
import { StylableTableCellNode } from "./stylable-table-cell-node";
import { $convertStylableTableCellElement } from "./stylable-table-cell-utils";

export class StylableTableCellMui extends StylableTableCellNode {
  static getType(): string {
    return super.getType();
  }

  static clone(node: StylableTableCellMui): StylableTableCellMui {
    return new this(node.__headerState, node.__colSpan, node.__width, node.__key);
  }

  getIsIndividualPadding(): boolean {
    return this.__custom_padding?.method === "individual";
  }

  getCommonPadding(): number {
    // If not individual padding previously, then return the highest of the individual values for all sides
    return this.__custom_padding?.method === "individual"
      ? this.__custom_padding.common
      : Math.max(
          this.__custom_padding?.left ?? 0,
          this.__custom_padding?.right ?? 0,
          this.__custom_padding?.top ?? 0,
          this.__custom_padding?.bottom ?? 0,
        );
  }

  private getWritableCustomPadding(): {
    left: number;
    right: number;
    top: number;
    bottom: number;
    common: number;
    method: "individual" | "common";
  } {
    const writable = this.getWritable();
    if (!writable.__custom_padding) {
      throw new Error("Custom padding not set");
    }
    return writable.__custom_padding;
  }

  private getReadableCustomPadding(): {
    left: number;
    right: number;
    top: number;
    bottom: number;
    common: number;
    method: "individual" | "common";
  } {
    if (!this.__custom_padding) {
      throw new Error("Custom padding not set");
    }
    return this.__custom_padding;
  }
  setPaddingLeft(padding_left: number): void {
    this.getWritableCustomPadding().left = padding_left;
  }

  setPaddingRight(padding_right: number): void {
    this.getWritableCustomPadding().right = padding_right;
  }
  setPaddingTop(padding_top: number): void {
    this.getWritableCustomPadding().top = padding_top;
  }
  setPaddingBottom(padding_bottom: number): void {
    this.getWritableCustomPadding().bottom = padding_bottom;
  }

  getPaddingLeft(): number {
    return this.getReadableCustomPadding().left;
  }
  getPaddingRight(): number {
    return this.getReadableCustomPadding().right;
  }
  getPaddingTop(): number {
    return this.getReadableCustomPadding().top;
  }
  getPaddingBottom(): number {
    return this.getReadableCustomPadding().bottom;
  }

  static importJSON(serializedNode: SerializedStylableTableCellNode): StylableTableCellMui {
    return super.importJSON(serializedNode) as StylableTableCellMui;
  }
  exportJSON(): SerializedStylableTableCellNode | SerializedTableCellNode {
    return super.exportJSON();
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    return super.exportDOM(editor);
  }

  static importDOM(): DOMConversionMap | null {
    return {
      td: (node) => ({
        conversion: () => $convertStylableTableCellElement(node, $createStylableTableCellMui),
        priority: 0,
      }),
      th: (node) => ({
        conversion: () => $convertStylableTableCellElement(node, $createStylableTableCellMui),
        priority: 0,
      }),
    };
  }
}

export function $createStylableTableCellMui(
  styleableColumnConfig?: StyleableColumnConfig | null,
  headerState?: number,
  colSpan?: number,
  width?: number,
  key?: NodeKey,
): StylableTableCellMui {
  const tableCellNode = new StylableTableCellMui(headerState, colSpan, width, key);
  // Set the padding for the table cell
  if (
    !styleableColumnConfig?.column_individual_padding &&
    typeof styleableColumnConfig?.column_padding === "number"
  ) {
    tableCellNode.setCommonPadding(styleableColumnConfig.column_padding);
  }
  if (styleableColumnConfig?.column_individual_padding) {
    const paddings = makeColumnPaddings(styleableColumnConfig);
    tableCellNode.setIndividualPadding(paddings);
  }

  return tableCellNode;
}

export function $isStylableTableCellMui(
  node: LexicalNode | null | undefined,
): node is StylableTableCellMui {
  return node instanceof StylableTableCellMui;
}
